import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "../api";
import { supabase } from "../supabaseClient";
import { QRCodeSVG } from "qrcode.react";
import {
    buildCatalogResponse,
    formatMoney,
    formatMoneyFromCents,
    quoteSelection,
} from "../utils/passCatalog";
import { LEGAL_VERSION, LegalConsentGate } from "../components/LegalDocumentModal";

const PAGE_STATE_KEY = "bbp_pass_web_state_v5";
const SHARE_EMAIL_KEY = "bbp_pass_share_email_drafts_v1";

const pad2 = (n) => String(n).padStart(2, "0");
const localYMD = (d = new Date()) =>
    `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;

const formatMDY = (ymd) => {
    if (!ymd) return "—";

    const [y, m, d] = String(ymd).split("-").map(Number);

    if (!y || !m || !d) return String(ymd);

    return `${m}/${d}/${y}`;
};

function addDaysLocal(startYmd, daysToAdd) {
    if (!startYmd) return "";
    const [y, m, d] = String(startYmd).split("-").map(Number);
    const dt = new Date(y, (m || 1) - 1, d || 1);
    dt.setDate(dt.getDate() + Number(daysToAdd || 0));
    return localYMD(dt);
}

function moneyToCentsLocal(value) {
    return Math.round(Number(value || 0) * 100);
}

function passStatusLabel(p) {
    const st = String(p?.status || "").toLowerCase();
    const today = localYMD();

    const start = String(p?.start_date || "");
    const end = String(p?.end_date || "");
    const plannedStart = String(p?.planned_start_date || "");
    const plannedEnd = String(p?.planned_end_date || "");

    if (st === "unactivated" || !start || !end) {
        if (plannedStart && today < plannedStart) return "Not Redeemable Yet";
        if (plannedEnd && today > plannedEnd) return "Trip Window Passed";
        return "Ready to Activate";
    }

    if (today < start) return "Not Redeemable";
    if (today > end) return "Expired";
    return "Active";
}

function getInviteDisplayTitle(invite) {
    const status = String(invite?.status || "").toLowerCase();
    const holderName = String(invite?.claimed_pass?.holder_name || "").trim();

    if (status === "claimed" || invite?.claimed_user_pass_id) {
        return holderName || "Claimed Adult Pass";
    }

    return invite?.pass_types?.name || "Adult Pass";
}

function pickCurrentAdultPass(passes) {
    if (!Array.isArray(passes) || passes.length === 0) return null;

    const today = localYMD();
    const adultPasses = passes.filter(
        (p) =>
            String(p?.holder_type || "adult").toLowerCase() !== "child" &&
            !p?.shared_access &&
            !p?.managed_outbound
    );

    const active = adultPasses.find(
        (p) => p?.start_date && p?.end_date && p.start_date <= today && today <= p.end_date
    );
    if (active) return active;

    const readyOrUpcoming = adultPasses
        .filter((p) => {
            const st = String(p?.status || "").toLowerCase();
            const plannedEnd = String(p?.planned_end_date || "");
            if (!(st === "unactivated" || !p?.start_date || !p?.end_date)) return false;
            if (plannedEnd && today > plannedEnd) return false;
            return true;
        })
        .sort((a, b) => {
            const aStart = String(a?.planned_start_date || "");
            const bStart = String(b?.planned_start_date || "");
            if (aStart && bStart && aStart !== bStart) return aStart.localeCompare(bStart);
            return new Date(b.created_at || 0) - new Date(a.created_at || 0);
        })[0];

    if (readyOrUpcoming) return readyOrUpcoming;

    return (
        adultPasses
            .filter((p) => {
                const plannedEnd = String(p?.planned_end_date || p?.end_date || "");
                return plannedEnd && today > plannedEnd;
            })
            .sort((a, b) => String(b?.planned_end_date || b?.end_date || "").localeCompare(String(a?.planned_end_date || a?.end_date || "")))[0] || null
    );
}

function sortPassesForManager(passes = []) {
    return [...passes].sort((a, b) => {
        const aStatus = passStatusLabel(a);
        const bStatus = passStatusLabel(b);

        const priority = {
            Active: 0,
            "Ready to Activate": 1,
            "Not Redeemable Yet": 2,
            "Trip Window Passed": 3,
            Expired: 4,
        };

        const pa = priority[aStatus] ?? 9;
        const pb = priority[bStatus] ?? 9;
        if (pa !== pb) return pa - pb;

        const aStart = String(a?.planned_start_date || a?.start_date || "");
        const bStart = String(b?.planned_start_date || b?.start_date || "");
        if (aStart && bStart && aStart !== bStart) return aStart.localeCompare(bStart);

        return new Date(b.created_at || 0) - new Date(a.created_at || 0);
    });
}

function extractClaimToken(raw = "") {
    const value = String(raw || "").trim();
    if (!value) return "";

    try {
        if (value.includes("?") || value.startsWith("http")) {
            const url = new URL(value, window.location.origin);
            return url.searchParams.get("claim") || value;
        }
    } catch {
        return value;
    }

    return value;
}

function PassManagerCard({
    pass,
    index,
    isCurrent,
    onOpenPass,
    onCopy,
}) {
    const isChild = String(pass?.holder_type || "adult").toLowerCase() === "child";
    const status = passStatusLabel(pass);
    const holderLabel =
        pass.holder_name ||
        (isChild ? "Child Pass" : pass.shared_access ? `Big Bum Pass ${index}` : `Big Bum Pass ${index}`);

    return (
        <div className={`managed-pass-card managed-pass-card--compact ${isCurrent ? "is-featured" : ""}`}>
            <div className="managed-pass-card__top">
                <div>
                    <div className="passes-card-kicker">
                        {isChild ? "Crew Pass" : pass.shared_access ? "" : "Big Bum Pass"}
                    </div>
                    <h3>{holderLabel}</h3>
                    <p className="passes-web-sub">{pass.pass_types?.name || "Beach Bum Pass"}</p>
                </div>

                <div className={`managed-pass-status managed-pass-status--${status.toLowerCase().replaceAll(" ", "-")}`}>
                    {status}
                </div>
            </div>

            <div className="managed-pass-meta managed-pass-meta--compact">
                <div>
                    <span>Trip Window</span>
                    <strong>{formatMDY(pass.planned_start_date)} → {formatMDY(pass.planned_end_date)}</strong>
                </div>
                <div>
                    <span>Active Window</span>
                    <strong>
                        {pass.start_date && pass.end_date
                            ? `${formatMDY(pass.start_date)} → ${formatMDY(pass.end_date)}`
                            : "Activates on first scan"}
                    </strong>
                </div>
            </div>

            <div className="managed-pass-actions managed-pass-actions--compact">
                <button className="passes-web-btn primary" type="button" onClick={() => onOpenPass(pass)}>
                    Open Pass
                </button>
                <button className="passes-web-btn secondary" type="button" onClick={() => onCopy(pass.id, "Pass ID")}>
                    Copy ID
                </button>
            </div>
        </div>
    );
}

function PassDetailModal({
    pass,
    emailValue,
    onEmailChange,
    onSendEmail,
    sending,
    onCopy,
    onClose,
}) {
    if (!pass) return null;

    const isChild = String(pass?.holder_type || "adult").toLowerCase() === "child";
    const status = passStatusLabel(pass);
    const holderLabel = pass.holder_name || (isChild ? "Child Pass" : "Big Bum Pass");
    const canShare = !isChild && !pass.shared_access;

    return (
        <div className="pass-modal-backdrop" role="presentation" onMouseDown={onClose}>
            <div
                className="pass-modal-card"
                role="dialog"
                aria-modal="true"
                aria-label={`${holderLabel} details`}
                onMouseDown={(event) => event.stopPropagation()}
            >
                <button className="pass-modal-close" type="button" onClick={onClose} aria-label="Close pass details">
                    ×
                </button>

                <div className="pass-modal-head">
                    <div>
                        <div className="passes-card-kicker">{isChild ? "Crew Pass" : pass.shared_access ? "" : "Managed Big Bum Pass"}</div>
                        <h2>{holderLabel}</h2>
                        <p>{pass.pass_types?.name || "Beach Bum Pass"}</p>
                    </div>
                    <div className={`managed-pass-status managed-pass-status--${status.toLowerCase().replaceAll(" ", "-")}`}>
                        {status}
                    </div>
                </div>

                <div className="pass-modal-body">
                    <div className="pass-modal-qr-shell">
                        <QRCodeSVG value={JSON.stringify({ pass_uuid: pass.id })} size={236} includeMargin />
                        <div className="pass-modal-qr-note">Show this QR when this specific pass is being scanned.</div>
                    </div>

                    <div className="managed-pass-meta pass-modal-meta">
                        <div>
                            <span>Trip Window</span>
                            <strong>{pass.planned_start_date || "—"} → {pass.planned_end_date || "—"}</strong>
                        </div>
                        <div>
                            <span>Active Window</span>
                            <strong>{pass.start_date && pass.end_date ? `${pass.start_date} → ${pass.end_date}` : "Activates on first scan"}</strong>
                        </div>
                        <div>
                            <span>Pass ID</span>
                            <strong className="mono">{pass.id}</strong>
                        </div>
                        {isChild ? (
                            <div>
                                <span>Age</span>
                                <strong>{pass.holder_age ?? "—"}</strong>
                            </div>
                        ) : null}
                    </div>
                </div>

                {canShare ? (
                    <div className="managed-pass-share managed-pass-share--modal">
                        <div className="passes-field">
                            <label>Email this pass claim code</label>
                            <input
                                type="email"
                                placeholder="recipient@email.com"
                                value={emailValue || ""}
                                onChange={(e) => onEmailChange(pass.id, e.target.value)}
                            />
                            <div className="passes-inline-note">
                                This creates a claim code for this pass. Once claimed, the pass belongs to the recipient account while staying viewable here.
                            </div>
                        </div>

                        <button
                            className="passes-web-btn secondary"
                            type="button"
                            disabled={sending}
                            onClick={() => onSendEmail(pass.id)}
                        >
                            {sending ? "Sending…" : "Send Code"}
                        </button>
                    </div>
                ) : null}

                <div className="managed-pass-actions pass-modal-actions">
                    <button className="passes-web-btn secondary" type="button" onClick={() => onCopy(pass.id, "Pass ID")}>
                        Copy Pass ID
                    </button>

                </div>
            </div>
        </div>
    );
}

export default function PassesWeb() {
    const [session, setSession] = useState(null);
    const [ready, setReady] = useState(false);

    const [passes, setPasses] = useState([]);
    const [claims, setClaims] = useState({ invites: [], child_passes: [] });
    const [catalog, setCatalog] = useState(() => buildCatalogResponse({ pass_types: [] }));
    const [loadingCatalog, setLoadingCatalog] = useState(true);

    const [status, setStatus] = useState("");
    const [checkoutResult, setCheckoutResult] = useState(null);
    const [submittingKey, setSubmittingKey] = useState("");
    const [claimToken, setClaimToken] = useState("");
    const [childDrafts, setChildDrafts] = useState({});
    const [shareEmailDrafts, setShareEmailDrafts] = useState({});
    const [selectedManagedPass, setSelectedManagedPass] = useState(null);
    const [legalAccepted, setLegalAccepted] = useState(false);

    const [selectedPassTypeId, setSelectedPassTypeId] = useState("");
    const [tripStartDate, setTripStartDate] = useState(localYMD());

    const [adultQty, setAdultQty] = useState(1);
    const [babyBumQty, setBabyBumQty] = useState(0);
    const [littleBumQty, setLittleBumQty] = useState(0);
    const [grommettQty, setGrommettQty] = useState(0);

    const [deliveryMode, setDeliveryMode] = useState("self");
    const [inviteEmail, setInviteEmail] = useState("");

    const nav = useNavigate();
    const location = useLocation();

    useEffect(() => {
        let alive = true;

        (async () => {
            const { data } = await supabase.auth.getSession();
            if (!alive) return;
            setSession(data?.session ?? null);
            setReady(true);
        })();

        const { data: sub } = supabase.auth.onAuthStateChange((_event, nextSession) => {
            setSession(nextSession ?? null);
        });

        return () => {
            alive = false;
            sub?.subscription?.unsubscribe?.();
        };
    }, []);

    useEffect(() => {
        let alive = true;
        (async () => {
            setLoadingCatalog(true);
            try {
                const { data } = await axios.get("/pass-types/catalog");
                if (!alive) return;
                setCatalog(buildCatalogResponse(data));
            } catch (error) {
                console.error("Pass catalog load failed", error?.response?.data || error);
            } finally {
                if (alive) setLoadingCatalog(false);
            }
        })();

        return () => {
            alive = false;
        };
    }, []);

    const availablePasses = useMemo(() => {
        const options = [];
        if (catalog.singleDay?.id) options.push(catalog.singleDay);
        options.push(
            ...catalog.multiDay.filter((passType) => {
                const days = Number(passType?.duration_days || 0);
                return days >= 2 && days <= 10;
            })
        );
        return options.sort((a, b) => Number(a?.duration_days || 0) - Number(b?.duration_days || 0));
    }, [catalog.singleDay, catalog.multiDay]);

    useEffect(() => {
        if (!selectedPassTypeId && availablePasses[0]?.id) {
            setSelectedPassTypeId(availablePasses[0].id);
        }
    }, [availablePasses, selectedPassTypeId]);

    useEffect(() => {
        try {
            const raw = localStorage.getItem(PAGE_STATE_KEY);
            if (!raw) return;

            const saved = JSON.parse(raw);
            const today = localYMD();

            if (saved.selectedPassTypeId) setSelectedPassTypeId(saved.selectedPassTypeId);
            if (saved.tripStartDate && saved.tripStartDate >= today) setTripStartDate(saved.tripStartDate);
            else setTripStartDate(today);

            if (typeof saved.adultQty === "number") setAdultQty(Math.min(10, Math.max(1, saved.adultQty)));
            if (typeof saved.babyBumQty === "number") setBabyBumQty(saved.babyBumQty);
            if (typeof saved.littleBumQty === "number") setLittleBumQty(saved.littleBumQty);
            if (typeof saved.grommettQty === "number") setGrommettQty(saved.grommettQty);
            if (saved.deliveryMode) setDeliveryMode(saved.deliveryMode);
            if (saved.inviteEmail) setInviteEmail(saved.inviteEmail);
            if (saved.claimToken) setClaimToken(saved.claimToken);
        } catch {
            // ignore broken saved state
        }
    }, []);

    useEffect(() => {
        try {
            const raw = localStorage.getItem(SHARE_EMAIL_KEY);
            if (raw) setShareEmailDrafts(JSON.parse(raw));
        } catch {
            // ignore broken saved state
        }
    }, []);

    useEffect(() => {
        localStorage.setItem(
            PAGE_STATE_KEY,
            JSON.stringify({
                selectedPassTypeId,
                tripStartDate,
                adultQty,
                babyBumQty,
                littleBumQty,
                grommettQty,
                deliveryMode,
                inviteEmail,
                claimToken,
            })
        );
    }, [
        selectedPassTypeId,
        tripStartDate,
        adultQty,
        babyBumQty,
        littleBumQty,
        grommettQty,
        deliveryMode,
        inviteEmail,
        claimToken,
    ]);

    useEffect(() => {
        localStorage.setItem(SHARE_EMAIL_KEY, JSON.stringify(shareEmailDrafts));
    }, [shareEmailDrafts]);

    const loadData = async () => {
        if (!session?.access_token) {
            setPasses([]);
            setClaims({ invites: [], child_passes: [] });
            return;
        }

        try {
            const [passesRes, claimsRes] = await Promise.all([
                axios.get("/passes"),
                axios.get("/pass-claims/mine"),
            ]);

            setPasses(Array.isArray(passesRes?.data) ? passesRes.data : []);
            setClaims({
                invites: Array.isArray(claimsRes?.data?.invites) ? claimsRes.data.invites : [],
                child_passes: Array.isArray(claimsRes?.data?.child_passes) ? claimsRes.data.child_passes : [],
            });
        } catch (error) {
            console.error("PassesWeb load error", error?.response?.data || error);
            setStatus("Could not load your pass details right now.");
        }
    };

    useEffect(() => {
        loadData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [session?.access_token]);

    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const claim = params.get("claim");
        if (claim) {
            setClaimToken(claim);
            localStorage.setItem(PAGE_STATE_KEY, JSON.stringify({ claimToken: claim }));
        }
    }, [location.search]);

    useEffect(() => {
        if (!ready) return;

        const params = new URLSearchParams(location.search);
        const checkoutState = params.get("checkout");
        const sessionId = params.get("session_id");

        if (checkoutState === "cancelled") {
            setStatus("Checkout was cancelled. Your pass selection is still here whenever you are ready.");
            return;
        }

        if (checkoutState !== "success" || !sessionId) return;
        if (!session?.access_token) return;

        let alive = true;

        (async () => {
            try {
                const { data } = await axios.get(
                    `/checkout/session-status?session_id=${encodeURIComponent(sessionId)}`
                );

                if (!alive) return;

                setCheckoutResult(data);
                setStatus("Payment complete. Your pass order is ready.");
                await loadData();

                window.history.replaceState({}, "", window.location.pathname);
            } catch (error) {
                console.error("Checkout status lookup failed", error?.response?.data || error);
                if (!alive) return;
                setStatus("Payment finished, but we could not refresh your pass order yet. Please reload in a moment.");
            }
        })();

        return () => {
            alive = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ready, session?.access_token, location.search]);

    const currentPass = useMemo(() => pickCurrentAdultPass(passes), [passes]);

    const adultPasses = useMemo(
        () => sortPassesForManager(passes.filter((p) => String(p?.holder_type || "adult").toLowerCase() !== "child")),
        [passes]
    );

    const childPasses = useMemo(
        () => sortPassesForManager(passes.filter((p) => String(p?.holder_type || "adult").toLowerCase() === "child")),
        [passes]
    );

    const managedAdultPasses = useMemo(
        () =>
            adultPasses.filter((pass) => {
                if (currentPass?.id && pass.id === currentPass.id) return false;

                // Purchaser/main adult passes have no holder name and live in the hero.
                // Pass Manager only shows additional adult passes or shared/outbound access passes.
                if (pass.shared_access || pass.managed_outbound) return true;
                return !!String(pass.holder_name || "").trim();
            }),
        [adultPasses, currentPass?.id]
    );

    const managedPassCount = managedAdultPasses.length + childPasses.length;

    const activeManagedPass = useMemo(() => {
        if (!selectedManagedPass?.id) return null;
        return [...managedAdultPasses, ...childPasses].find((pass) => pass.id === selectedManagedPass.id) || selectedManagedPass;
    }, [selectedManagedPass, managedAdultPasses, childPasses]);

    const childSlots = useMemo(
        () => claims.invites.filter((invite) => invite.invite_kind === "child_slot" && invite.status === "pending"),
        [claims.invites]
    );

    const passById = useMemo(() => {
        return new Map((passes || []).map((pass) => [pass.id, pass]));
    }, [passes]);

    const adultInvites = useMemo(
        () =>
            claims.invites
                .filter((invite) => invite.invite_kind === "adult_invite")
                .map((invite) => ({
                    ...invite,
                    claimed_pass: invite.claimed_user_pass_id
                        ? passById.get(invite.claimed_user_pass_id) || null
                        : null,
                })),
        [claims.invites, passById]
    );

    const selectedPass = useMemo(
        () => availablePasses.find((passType) => passType.id === selectedPassTypeId) || availablePasses[0] || null,
        [availablePasses, selectedPassTypeId]
    );

    const childOptions = useMemo(() => {
        if (!selectedPass) {
            return { baby_bum: null, little_bum: null, grommett: null };
        }
        return catalog.childByDurationAndBand[String(selectedPass.duration_days)] || {
            baby_bum: null,
            little_bum: null,
            grommett: null,
        };
    }, [catalog.childByDurationAndBand, selectedPass]);

    const matchedBabyBumPass = childOptions.baby_bum || null;
    const matchedLittleBumPass = childOptions.little_bum || null;
    const matchedGrommettPass = childOptions.grommett || null;

    const tripEndDate = selectedPass
        ? addDaysLocal(tripStartDate, Number(selectedPass.duration_days || 1) - 1)
        : tripStartDate;

    const purchaseQuote = useMemo(() => {
        const base = quoteSelection({
            adultPassType: selectedPass,
            babyBumPassType: matchedBabyBumPass,
            babyBumQty,
            littleBumPassType: matchedLittleBumPass,
            littleBumQty,
            grommettPassType: matchedGrommettPass,
            grommettQty,
            taxRate: 0,
        });

        const safeAdultQty = Math.max(1, Number(adultQty || 1));
        const adultUnitCents = moneyToCentsLocal(selectedPass?.price || 0);
        const extraAdultCents = adultUnitCents * Math.max(0, safeAdultQty - 1);
        const subtotalCents = Number(base.subtotalCents || 0) + extraAdultCents;

        return {
            ...base,
            adultQty: safeAdultQty,
            adultUnitCents,
            adultSubtotalCents: adultUnitCents * safeAdultQty,
            subtotalCents,
            taxCents: 0,
            totalCents: subtotalCents,
        };
    }, [
        selectedPass,
        matchedBabyBumPass,
        babyBumQty,
        matchedLittleBumPass,
        littleBumQty,
        matchedGrommettPass,
        grommettQty,
        adultQty,
    ]);

    const setShareEmailForPass = (passId, value) => {
        setShareEmailDrafts((current) => ({ ...current, [passId]: value }));
    };

    const copyText = async (value, label = "Text") => {
        try {
            await navigator.clipboard.writeText(String(value || ""));
            setStatus(`${label} copied.`);
        } catch {
            setStatus(`Could not copy ${label.toLowerCase()} on this device.`);
        }
    };

    const redirectToLogin = (message = "Create your account or log in to purchase a pass.") => {
        sessionStorage.setItem("bbp_login_notice", message);
        const next = claimToken ? `/passes?claim=${encodeURIComponent(claimToken)}` : "/passes";
        nav(`/login?next=${encodeURIComponent(next)}`);
    };

    const handleClaim = async () => {
        const token = extractClaimToken(claimToken);

        if (!token) {
            setStatus("Paste the invite token or full claim link you were sent.");
            return;
        }

        if (!session?.access_token) {
            localStorage.setItem(PAGE_STATE_KEY, JSON.stringify({ claimToken: token }));
            redirectToLogin("Log in or create an account to claim your Beach Bum Pass.");
            return;
        }

        setSubmittingKey("claim");
        setStatus("");

        try {
            await axios.post("/pass-claims/claim", { token });
            setStatus("Pass claimed. It will activate on your first scan.");
            setClaimToken("");
            await loadData();
        } catch (error) {
            console.error("Pass claim failed", error?.response?.data || error);
            setStatus(error?.response?.data?.error || "Could not claim that pass yet.");
        } finally {
            setSubmittingKey("");
        }
    };

    const beginCheckout = async () => {
        if (!session?.access_token) {
            redirectToLogin();
            return;
        }

        if (!selectedPass) {
            setStatus("Choose a pass length before continuing.");
            return;
        }

        if (!legalAccepted) {
            setStatus("Review and accept the BBP Terms and Conditions before heading to secure payment.");
            return;
        }

        setStatus("");
        setSubmittingKey("purchase");

        try {
            const purchaseMode = Number(selectedPass.duration_days || 1) === 1 ? "single_day" : "multi_day";

            const { data } = await axios.post("/checkout/create-session", {
                purchase_mode: purchaseMode,
                adult_pass_type_id: selectedPass.id,
                adult_qty: adultQty,
                baby_bum_pass_type_id: matchedBabyBumPass?.id || null,
                baby_bum_qty: babyBumQty,
                little_bum_pass_type_id: matchedLittleBumPass?.id || null,
                little_bum_qty: littleBumQty,
                grommett_pass_type_id: matchedGrommettPass?.id || null,
                grommett_qty: grommettQty,
                planned_start_date: tripStartDate,
                planned_end_date: tripEndDate,
                adult_delivery: "self",
                adult_invite_email: null,
                legal_accepted: true,
                legal_version: LEGAL_VERSION,
                legal_accepted_at: new Date().toISOString(),
            });

            if (!data?.checkout_url) {
                throw new Error("Checkout URL missing from server response.");
            }

            window.location.assign(data.checkout_url);
        } catch (error) {
            console.error("Checkout create failed", error?.response?.data || error);
            setStatus(error?.response?.data?.error || "Could not start checkout right now.");
            setSubmittingKey("");
        }
    };

    const sendManagedPassEmail = async (passId) => {
        const to = String(shareEmailDrafts[passId] || "").trim();

        if (!to) {
            setStatus("Enter an email address before sending this pass code.");
            return;
        }

        setSubmittingKey(`share-${passId}`);
        setStatus("");

        try {
            const { data } = await axios.post(`/passes/${passId}/share-email`, { to });
            setStatus(data?.message || `Pass code sent to ${to}.`);
        } catch (error) {
            console.error("Managed pass email failed", error?.response?.data || error);
            setStatus(error?.response?.data?.error || error?.response?.data?.message || "Could not send this pass email.");
        } finally {
            setSubmittingKey("");
        }
    };

    const assignChildPass = async (inviteId) => {
        const draft = childDrafts[inviteId] || {};
        const childName = String(draft.name || "")
            .trim()
            .replace(/\s+/g, " ")
            .slice(0, 80);
        const rawAge = String(draft.age || "").trim();
        const childAge = Number.parseInt(rawAge, 10);

        if (!childName) {
            setStatus("Add a child name before assigning the pass.");
            return;
        }

        const invite = childSlots.find((item) => item.id === inviteId);
        const ageMin = Number.isFinite(Number(invite?.pass_types?.age_min))
            ? Number(invite.pass_types.age_min)
            : 0;
        const ageMax = Number.isFinite(Number(invite?.pass_types?.age_max))
            ? Number(invite.pass_types.age_max)
            : 17;

        if (!Number.isInteger(childAge) || childAge < ageMin || childAge > ageMax) {
            setStatus(`Add a valid age between ${ageMin} and ${ageMax}.`);
            return;
        }

        setSubmittingKey(`child-${inviteId}`);
        setStatus("");

        try {
            await axios.post(`/pass-claims/${inviteId}/assign-child`, {
                child_name: childName,
                child_age: childAge,
            });

            setStatus(`${childName}'s child pass is assigned and waiting for the parent pass to activate first.`);
            setChildDrafts((current) => ({
                ...current,
                [inviteId]: { name: "", age: "" },
            }));

            await loadData();
        } catch (error) {
            console.error("Assign child pass failed", error?.response?.data || error);
            setStatus(error?.response?.data?.error || "Could not assign that child pass.");
        } finally {
            setSubmittingKey("");
        }
    };

    if (!ready) {
        return <div className="page-center">Loading…</div>;
    }

    return (
        <div className="passes-web passes-web-page">
            <section className={`passes-web-hero ${currentPass ? "passes-web-hero--has-pass" : "passes-web-hero--empty-pass"}`}>
                <div className="passes-web-hero__copy">

                    <h1>{currentPass ? "Your Beach Bum Pass is ready." : "Chart your stretch of the Emerald Coast."}</h1>
                    <p>
                        {currentPass
                            ? "Show this QR at your first eligible stop to activate your pass. Manage every adult and crew pass below from one clean window."
                            : "Build your pass bundle, add Grommet, Little Bum, and Baby Bum Passes for the younger crew, and keep every stop lined up from your first landing to your final sunset."}
                    </p>

                    <div className="passes-web-cta-row">
                        {!session ? (
                            <>
                                <a className="passes-web-cta primary" href="/register">Get Started</a>
                                <a className="passes-web-cta secondary" href={`/login?next=${encodeURIComponent("/passes")}`}>Log in to purchase</a>
                            </>
                        ) : (
                            <>
                                <a className="passes-web-cta primary" href="#pass-manager">Manage Passes</a>
                                <a className="passes-web-cta secondary" href="#buy-passes">Build More Passes</a>
                            </>
                        )}
                    </div>
                </div>

                <div className="passes-web-hero-visual">
                    {session && currentPass ? (
                        <div className="passes-web-hero-qr-card">
                            <div className="passes-web-hero-qr-eyebrow">Featured Pass</div>
                            <div className="passes-web-hero-qr-shell">
                                <QRCodeSVG value={JSON.stringify({ pass_uuid: currentPass.id })} size={188} includeMargin />
                            </div>
                            <div className="passes-web-hero-qr-name">
                                {currentPass.holder_name || currentPass.pass_types?.name || "Beach Bum Pass"}
                            </div>
                            <div className="passes-web-hero-qr-status">{passStatusLabel(currentPass)}</div>
                            <div className="passes-web-hero-qr-window">
                                {currentPass.planned_start_date || "—"} → {currentPass.planned_end_date || "—"}
                            </div>
                            <button
                                className="passes-web-hero-qr-copy"
                                type="button"
                                onClick={() => copyText(currentPass.id, "Pass ID")}
                            >
                                Copy Pass ID
                            </button>
                        </div>
                    ) : (
                        <div className="passes-web-hero-qr-card passes-web-hero-qr-card--placeholder">
                            <div className="passes-web-hero-qr-eyebrow">Pass QR</div>
                            <div className="passes-web-hero-qr-placeholder"><span>QR</span></div>
                            <div className="passes-web-hero-qr-name">Ready after checkout</div>
                            <div className="passes-web-hero-qr-window">
                                Your pass code will dock here once your purchase is complete.
                            </div>
                        </div>
                    )}
                </div>
            </section>

            {status ? <div className="passes-web-status">{status}</div> : null}

            {checkoutResult?.order ? (
                <div className="purchase-status-panel">
                    <div className="purchase-status-panel__badge">
                        {checkoutResult.payment_status === "paid" ? "Payment Received" : "Processing"}
                    </div>
                    <div className="purchase-status-panel__body">
                        <strong>Order Status:</strong> {checkoutResult.order?.status || "pending"}
                    </div>
                </div>
            ) : null}

            <section className="passes-web-section" id="buy-passes">
                <div className="passes-web-section-head">

                </div>

                <div className="passes-web-card passes-web-card--builder">
                    <div className="passes-card-head">
                        <h2><strong>Chart Your Beach Bum Pass</strong></h2>
                        <div className="passes-web-sub">
                            Build your Beach Bum Pass for the adventure ahead! Pick how many days you’re cruising the coast, add passes for your beach crew.
                        </div>
                    </div>

                    <div className="pass-builder-stack">
                        <div className="passes-field">
                            <label>How many days?</label>
                            <select
                                value={selectedPass?.id || ""}
                                onChange={(e) => setSelectedPassTypeId(e.target.value)}
                                disabled={loadingCatalog}
                            >
                                {availablePasses.map((passType) => (
                                    <option key={passType.id} value={passType.id}>
                                        {passType.duration_days} day{passType.duration_days === 1 ? "" : "s"} • {formatMoney(passType.price)}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div className="passes-field">
                            <label>When does your voyage begin?</label>
                            <input
                                type="date"
                                value={tripStartDate}
                                min={localYMD()}
                                onChange={(e) => setTripStartDate(e.target.value)}
                            />
                            <div className="passes-inline-note">
                                Voyage Window: {formatMDY(tripStartDate)} → {formatMDY(tripEndDate)}
                            </div>
                        </div>

                        <div className="passes-field">
                            <h4>Big Bum Passes • Ages 16+</h4>
                            <div className="pass-counter">
                                <button type="button" onClick={() => setAdultQty((qty) => Math.max(1, qty - 1))}>−</button>
                                <span>{adultQty}</span>
                                <button type="button" onClick={() => setAdultQty((qty) => Math.min(10, qty + 1))}>+</button>
                            </div>
                            {/* <div className="passes-inline-note">
                                {adultQty > 1
                                    ? "Additional Big Bum Passes stay available in this account and can be emailed from Pass Manager."
                                    : "Buy one for yourself, or increase the count for friends/family you want to manage here."}
                            </div>*/}
                        </div>

                        <div className="passes-field">
                            <h4>Grommet Passes • Ages 7–15</h4>
                            <div className="pass-counter">
                                <button type="button" onClick={() => setGrommettQty((qty) => Math.max(0, qty - 1))}>−</button>
                                <span>{grommettQty}</span>
                                <button type="button" onClick={() => setGrommettQty((qty) => Math.min(10, qty + 1))}>+</button>
                            </div>
                            {/* <div className="passes-inline-note">
                                {matchedGrommettPass
                                    ? `${formatMoney(matchedGrommettPass.price)} each for ${selectedPass?.duration_days || "selected"}-day Grommet passes.`
                                    : "Create Grommet pass types in your database."}
                            </div> */}
                        </div>

                        <div className="passes-field">
                            <h4>Little Bum Passes • Ages 5–6</h4>
                            <div className="pass-counter">
                                <button type="button" onClick={() => setLittleBumQty((qty) => Math.max(0, qty - 1))}>−</button>
                                <span>{littleBumQty}</span>
                                <button type="button" onClick={() => setLittleBumQty((qty) => Math.min(10, qty + 1))}>+</button>
                            </div>
                            {/*<div className="passes-inline-note">
                                {matchedLittleBumPass
                                    ? `${formatMoney(matchedLittleBumPass.price)} each for ${selectedPass?.duration_days || "selected"}-day Little Bum passes.`
                                    : "Create Little Bum pass types in your database."}
                            </div>*/}
                        </div>

                        <div className="passes-field">
                            <h4>Baby Bum Passes • Ages 0–3</h4>
                            <div className="pass-counter">
                                <button type="button" onClick={() => setBabyBumQty((qty) => Math.max(0, qty - 1))}>−</button>
                                <span>{babyBumQty}</span>
                                <button type="button" onClick={() => setBabyBumQty((qty) => Math.min(10, qty + 1))}>+</button>
                            </div>
                            {/* <div className="passes-inline-note">
                                {matchedBabyBumPass
                                    ? `${formatMoney(matchedBabyBumPass.price)} each for ${selectedPass?.duration_days || "selected"}-day Baby Bum passes.`
                                    : "Create Baby Bum pass types in your database."}
                            </div>*/}
                        </div>

                    </div>

                    <div className="pass-total-box">
                        <div><span>Big Bum Passes</span><strong>{selectedPass ? `${adultQty} × ${formatMoney(selectedPass.price)}` : "—"}</strong></div>
                        <div><span>Grommet Passes</span><strong>{grommettQty > 0 && matchedGrommettPass ? `${grommettQty} × ${formatMoney(matchedGrommettPass.price)}` : "$0.00"}</strong></div>
                        <div><span>Little Bum Passes</span><strong>{littleBumQty > 0 && matchedLittleBumPass ? `${littleBumQty} × ${formatMoney(matchedLittleBumPass.price)}` : "$0.00"}</strong></div>
                        <div><span>Baby Bum Passes</span><strong>{babyBumQty > 0 && matchedBabyBumPass ? `${babyBumQty} × ${formatMoney(matchedBabyBumPass.price)}` : "$0.00"}</strong></div>

                        <div><span>Tax</span><strong>Calculated at checkout</strong></div>
                        <div className="is-total"><span>Pass Subtotal</span><strong>{formatMoneyFromCents(purchaseQuote.subtotalCents)}</strong></div>
                    </div>

                    <LegalConsentGate accepted={legalAccepted} onAccept={() => setLegalAccepted(true)} />

                    <div className="secure-checkout-badges" aria-label="Secure payment information">
                        <span>🔒 Secure Checkout</span>
                        <span>🛡️ PCI-Compliant Processor</span>
                        <span>💳 Card Data Not Stored by BBP</span>
                    </div>

                    <div className="passes-actions">
                        <button
                            className="passes-web-btn primary"
                            type="button"
                            disabled={
                                !selectedPass ||
                                !legalAccepted ||
                                submittingKey === "purchase" ||
                                (babyBumQty > 0 && !matchedBabyBumPass) ||
                                (littleBumQty > 0 && !matchedLittleBumPass) ||
                                (grommettQty > 0 && !matchedGrommettPass)
                            }
                            onClick={beginCheckout}
                        >
                            {submittingKey === "purchase" ? "Redirecting…" : "Head to Checkout"}
                        </button>
                    </div>
                </div>
            </section>

            {session ? (
                <section className="passes-web-section" id="pass-manager">
                    <div className="passes-web-section-head">
                        <h2>Pass Manager</h2>
                        <p>
                            Keep your crew’s passes in one place, send passes to your beach crew, and quickly present QR codes at each stop.
                        </p>
                    </div>

                    {managedPassCount === 0 ? (
                        <div className="passes-web-card">
                            <h3>No additional passes yet</h3>
                            <p className="passes-web-sub">Your featured pass is shown above.</p>
                        </div>
                    ) : (
                        <div className="managed-pass-grid">
                            {managedAdultPasses.map((pass, index) => (
                                <PassManagerCard
                                    key={pass.id}
                                    pass={pass}
                                    index={index + 1}
                                    isCurrent={false}
                                    onOpenPass={setSelectedManagedPass}
                                    onCopy={copyText}
                                />
                            ))}

                            {childPasses.map((pass, index) => (
                                <PassManagerCard
                                    key={pass.id}
                                    pass={pass}
                                    index={index}
                                    isCurrent={false}
                                    onOpenPass={setSelectedManagedPass}
                                    onCopy={copyText}
                                />
                            ))}
                        </div>
                    )}
                </section>
            ) : null}

            <section className="passes-web-section" id="claim">
                <div className="passes-web-card passes-web-card--claim">
                    <div className="passes-card-head">
                        <div>
                            <div className="passes-web-badge">Claim a Pass</div>
                            <h3>Redeem an emailed pass</h3>
                            <p className="passes-web-sub">
                                Paste the claim token or full claim link from your email. After login, the pass will be added to your account.
                            </p>
                        </div>
                    </div>

                    <div className="passes-claim-row">
                        <input
                            type="text"
                            value={claimToken}
                            onChange={(e) => setClaimToken(e.target.value)}
                            placeholder="Paste invite token or claim link"
                        />
                        <button
                            className="passes-web-btn primary"
                            type="button"
                            disabled={submittingKey === "claim"}
                            onClick={handleClaim}
                        >
                            {submittingKey === "claim" ? "Claiming…" : session ? "Claim Pass" : "Log in to Claim"}
                        </button>
                    </div>
                </div>
            </section>

            {session ? (
                <>
                    <section className="passes-web-section" id="child-passes">
                        <div className="passes-web-section-head">
                            <h2>Crew Passes & Invites</h2>
                            <p>Assign the younger crew and keep every pass ready before you cast off.</p>
                        </div>

                        <div className="passes-web-card">
                            <div className="passes-card-head">
                                <div className="passes-web-badge">Crew Assignments</div>
                                <h3>Name and assign Grommet, Little Bum, and Baby Bum Passes</h3>
                            </div>

                            {childSlots.length > 0 ? (
                                <div className="child-pass-list">
                                    {childSlots.map((invite) => {
                                        const draft = childDrafts[invite.id] || { name: "", age: "" };
                                        const ageMin = Number.isFinite(Number(invite?.pass_types?.age_min))
                                            ? Number(invite.pass_types.age_min)
                                            : 0;
                                        const ageMax = Number.isFinite(Number(invite?.pass_types?.age_max))
                                            ? Number(invite.pass_types.age_max)
                                            : 17;

                                        return (
                                            <div key={invite.id} className="child-pass-item">
                                                <div>
                                                    <strong>{invite.pass_types?.name || "Child Pass"}</strong>
                                                    <div className="pass-option-sub">
                                                        <strong>{formatMDY(invite.planned_start_date)} → {formatMDY(invite.planned_end_date)}</strong>
                                                    </div>
                                                </div>

                                                <div className="child-pass-assign-grid">
                                                    <input
                                                        type="text"
                                                        placeholder="Child name"
                                                        maxLength={80}
                                                        value={draft.name}
                                                        onChange={(e) =>
                                                            setChildDrafts((current) => ({
                                                                ...current,
                                                                [invite.id]: {
                                                                    ...(current[invite.id] || {}),
                                                                    name: e.target.value,
                                                                },
                                                            }))
                                                        }
                                                    />
                                                    <input
                                                        type="number"
                                                        placeholder={`Age ${ageMin}-${ageMax}`}
                                                        inputMode="numeric"
                                                        min={ageMin}
                                                        max={ageMax}
                                                        value={draft.age}
                                                        onChange={(e) =>
                                                            setChildDrafts((current) => ({
                                                                ...current,
                                                                [invite.id]: {
                                                                    ...(current[invite.id] || {}),
                                                                    age: e.target.value,
                                                                },
                                                            }))
                                                        }
                                                    />
                                                </div>

                                                <button
                                                    className="passes-web-btn secondary"
                                                    type="button"
                                                    disabled={submittingKey === `child-${invite.id}`}
                                                    onClick={() => assignChildPass(invite.id)}
                                                >
                                                    {submittingKey === `child-${invite.id}` ? "Assigning…" : "Assign"}
                                                </button>
                                            </div>
                                        );
                                    })}
                                </div>
                            ) : (
                                <p className="passes-web-sub">No unassigned child passes right now.</p>
                            )}
                        </div>
                    </section>

                    <section className="passes-web-section">
                        <div className="passes-web-card">
                            <div className="passes-card-head">
                                <div className="passes-web-badge">Transferred Big Bum Passes</div>
                                <h3>Email claim links</h3>
                            </div>

                            {adultInvites.length === 0 ? (
                                <p className="passes-web-sub">No transferred Big Bum Passes yet.</p>
                            ) : (
                                <div className="invite-list">
                                    {adultInvites.map((invite) => (
                                        <div key={invite.id} className="invite-item">
                                            <div>
                                                {/* <strong>{invite.pass_types?.name || "Big Bum Pass"}</strong> */}
                                                <strong>{getInviteDisplayTitle(invite)}</strong>
                                                <div className="pass-option-sub">
                                                    {invite.invite_email || "Invite email not set"} • {invite.status}
                                                    {invite.claimed_user_pass_id ? ` • Claimed Pass: ${invite.claimed_user_pass_id}` : ""}
                                                </div>
                                            </div>
                                            {invite.claim_url && String(invite.status).toLowerCase() === "pending" ? (
                                                <button
                                                    className="passes-web-btn secondary"
                                                    type="button"
                                                    onClick={() => copyText(invite.claim_url, "Invite link")}
                                                >
                                                    Copy Invite Link
                                                </button>
                                            ) : null}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </section>
                </>
            ) : null}

            {activeManagedPass ? (
                <PassDetailModal
                    pass={activeManagedPass}
                    emailValue={shareEmailDrafts[activeManagedPass.id] || ""}
                    onEmailChange={setShareEmailForPass}
                    onSendEmail={sendManagedPassEmail}
                    sending={submittingKey === `share-${activeManagedPass.id}`}
                    onCopy={copyText}
                    onClose={() => setSelectedManagedPass(null)}
                />
            ) : null}
        </div>
    );
}
